import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  resetOnlineOrderState,
  verifyOnlineOrderRequest,
} from "../../redux/OnlineOrder/onlineOrderSlice";
import { getPaymentMeta } from "../Common/PaymentGatewayMethods";

const ORDER_ID_KEYS = [
  "order_id",
  "orderId",
  "Order_Id",
  "orderid",
  "ORDERID",
  "cf_order_id",
];

const extractOrderId = (search, paymentMeta) => {
  const params = new URLSearchParams(search);
  for (const key of ORDER_ID_KEYS) {
    const value = params.get(key);
    if (value && value !== "{order_id}") return value;
  }
  return paymentMeta?.orderId || "";
};

const getMetalName = (paymentMeta) => {
  if (typeof paymentMeta?.metal === "string") return paymentMeta.metal;
  return paymentMeta?.metal?.metalName || paymentMeta?.metalName || "";
};

const PaymentVerify = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [verifyRequested, setVerifyRequested] = useState(false);

  const onlineOrder = useSelector((state) => state.onlineOrder);
  const paymentMeta = getPaymentMeta();
  const orderId = extractOrderId(location.search, paymentMeta);
  const webhookReturn =
    location.pathname.toLowerCase().includes("webhookurl") ||
    new URLSearchParams(location.search).get("delay") === "1";
  const metalName = getMetalName(paymentMeta);
  const metalPurity = paymentMeta.metal?.karat
    ? `${paymentMeta.metal?.karat} ${paymentMeta.metal?.purity}`
    : paymentMeta.metalPurity || "";

  useEffect(() => {
    dispatch(resetOnlineOrderState());
  }, [dispatch]);

  useEffect(() => {
    if (!orderId || verifyRequested) return;

    const verifyTimer = setTimeout(() => {
      const verifyPayload = {
        signature: "",
        orderId,
        orderType: paymentMeta.orderType,
        data: "",
        weight: paymentMeta.grams,
        metalName: metalName || paymentMeta.metal,
        amount: paymentMeta.totalAmount,
        buyType: paymentMeta.buyType,
        transactionMode: paymentMeta.selectedGateway,
        planid: paymentMeta.planid || "",
        moneysip: Boolean(
          paymentMeta.moneysip ||
            metalName === "Money" ||
            paymentMeta.metal === "Money",
        ),
        x_api_version: "2022-09-01",
        jeweller: process.env.REACT_APP_JEWELLER_ID,
      };

      dispatch(verifyOnlineOrderRequest(verifyPayload));
      setVerifyRequested(true);
    }, webhookReturn ? 2000 : 0);

    return () => clearTimeout(verifyTimer);
  }, [dispatch, metalName, orderId, paymentMeta, verifyRequested, webhookReturn]);

  useEffect(() => {
    if (!verifyRequested || !orderId) return;

    const goToStatus = (isSuccess, invoiceUrl = "") => {
      navigate("/payment-status", {
        replace: true,
        state: {
          isSuccess,
          orderId,
          metalName,
          metalPurity,
          amount: paymentMeta.totalAmount,
          invoiceUrl,
        },
      });
    };

    if (onlineOrder.error) {
      goToStatus(false);
      return;
    }

    if (!onlineOrder.verifyData) return;

    const res = onlineOrder.verifyData;
    goToStatus(
      Boolean(res.success),
      res.success ? `${process.env.REACT_APP_INVOICE_URL}/invoice/${res.data}` : "",
    );
  }, [
    navigate,
    orderId,
    verifyRequested,
    onlineOrder.verifyData,
    onlineOrder.error,
    metalName,
    metalPurity,
    paymentMeta.totalAmount,
  ]);

  if (!orderId) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <h2>Unable to verify payment</h2>
        <p>Order reference is missing. Please go back and try again.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, textAlign: "center" }}>
      <h2>Processing Payment…</h2>
      <p>Please wait, verifying transaction with server...</p>
    </div>
  );
};

export default PaymentVerify;
